import React from "react";
import { Link } from "react-router-dom";
import { services } from "../data/services";
import { Mail, Phone, MapPin } from "lucide-react";
import { BRAND } from "../data/brand";
import Logo from "./Logo";
import TextZoomControl from "./TextZoomControl";
import { useBranding } from "../lib/branding";

const Footer = () => {
  const featuredServices = services.slice(0, 9);
  const branding = useBranding();

  const trustFacts = [
    BRAND.legalName && { label: "Registered as", value: BRAND.legalName },
    BRAND.companyNumber && { label: "Company no.", value: BRAND.companyNumber },
    BRAND.vatNumber && { label: "VAT no.", value: BRAND.vatNumber },
    BRAND.icoRegistration && { label: "ICO reg.", value: BRAND.icoRegistration },
    BRAND.yearFounded && { label: "Founded", value: BRAND.yearFounded },
  ].filter(Boolean);

  return (
    <footer data-testid="site-footer" className="ek-gradient-dark-soft text-white relative overflow-hidden">
      <div className="ek-star-watermark-left" />

      <div className="ek-container relative z-10 pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Logo variant="on-dark" size="lg" logoUrl={branding.logo_url} brandName={branding.name} color={branding.primary_color} />
            <p className="ek-heading text-2xl md:text-3xl mt-8 text-white leading-tight">
              Technology-driven transformation. Practical delivery. Measurable value.
            </p>
            <p className="mt-6 text-zinc-300 font-light leading-relaxed text-[15px]">
              Helping organisations modernise services, improve delivery confidence and realise measurable value from transformation.
            </p>
            <Link to="/contact" className="ek-btn ek-btn-primary mt-8" data-testid="footer-cta-book-discovery">
              Book a discovery conversation
            </Link>
          </div>

          <div className="lg:col-span-4">
            <div className="ek-eyebrow-light text-zinc-400">Services</div>
            <ul className="mt-5 space-y-2.5">
              {featuredServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-zinc-300 hover:text-white font-light text-[14.5px] transition-colors"
                    data-testid={`footer-service-link-${s.slug}`}
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services" className="text-[#A179FA] hover:text-white font-medium text-[14.5px] mt-2 inline-block">
                  View all services →
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="ek-eyebrow-light text-zinc-400">Company</div>
            <ul className="mt-5 space-y-2.5">
              <li><Link to="/about" className="text-zinc-300 hover:text-white font-light text-[14.5px]">About Ekselens</Link></li>
              <li><Link to="/how-it-works" className="text-zinc-300 hover:text-white font-light text-[14.5px]" data-testid="footer-how-it-works-link">How it all fits together</Link></li>
              <li><Link to="/system-of-proof-in-action" className="text-zinc-300 hover:text-white font-light text-[14.5px]" data-testid="footer-sopia-link">System of Proof in action</Link></li>
              <li><Link to="/insights" className="text-zinc-300 hover:text-white font-light text-[14.5px]">Insights</Link></li>
              <li><Link to="/resources" className="text-zinc-300 hover:text-white font-light text-[14.5px]">Resources</Link></li>
              <li><Link to="/help" className="text-zinc-300 hover:text-white font-light text-[14.5px]" data-testid="footer-help-link">Help &amp; methodology</Link></li>
              <li><Link to="/methodology" className="text-zinc-300 hover:text-white font-light text-[14.5px]" data-testid="footer-methodology-link">How the method works</Link></li>
              <li><Link to="/sub-processors" className="text-zinc-300 hover:text-white font-light text-[14.5px]" data-testid="footer-subprocessors-link">Sub-processors &amp; DPA</Link></li>
              <li><Link to="/data-retention" className="text-zinc-300 hover:text-white font-light text-[14.5px]" data-testid="footer-data-retention-link">Data retention</Link></li>
              <li><Link to="/contact" className="text-zinc-300 hover:text-white font-light text-[14.5px]">Contact</Link></li>
            </ul>

            <div className="ek-eyebrow-light text-zinc-400 mt-10">Get in touch</div>
            <ul className="mt-5 space-y-3 text-[14.5px]">
              <li className="flex items-start gap-3 text-zinc-300">
                <Mail size={16} className="mt-0.5 text-[#A179FA] shrink-0" />
                <a href="mailto:info@ekselens.com" className="hover:text-white">info@ekselens.com</a>
              </li>
              <li className="flex items-start gap-3 text-zinc-300">
                <Phone size={16} className="mt-0.5 text-[#A179FA] shrink-0" />
                <a href="tel:+442078747217" className="hover:text-white">+44 (0) 20 7874 7217</a>
              </li>
              <li className="flex items-start gap-3 text-zinc-300 font-light">
                <MapPin size={16} className="mt-0.5 text-[#A179FA] shrink-0" />
                <span>5th Floor, 167-169 Great Portland Street,<br />London, W1W 5PF</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-zinc-500 text-xs tracking-wide">© {new Date().getFullYear()} Ekselens Consulting. All rights reserved.</p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500">
            <li>
              <Link
                to="/accessibility"
                className="hover:text-white inline-flex items-center gap-1.5"
                data-testid="footer-accessibility-link"
                title="WCAG 2.1 AA conformance + VPAT request"
              >
                <span className="inline-block px-2 py-0.5 border border-zinc-700 text-zinc-300 text-[10px] tracking-[0.14em] uppercase">WCAG 2.1 AA</span>
              </Link>
            </li>
            <li><Link to="/privacy" className="hover:text-white">Privacy policy</Link></li>
            <li><Link to="/cookies" className="hover:text-white">Cookie policy</Link></li>
            <li>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("ek-open-cookie-consent"))}
                className="hover:text-white text-left"
                data-testid="footer-cookie-preferences"
              >
                Cookie preferences
              </button>
            </li>
            <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
            <li><Link to="/admin/login" className="hover:text-white" data-testid="footer-admin-link">Admin</Link></li>
          </ul>
        </div>

        {trustFacts.length > 0 ? (
          <div className="mt-6 pt-6 border-t border-white/5" data-testid="footer-trust-facts">
            <ul className="flex flex-wrap gap-x-8 gap-y-2 text-[11px] text-zinc-500">
              {trustFacts.map((f) => (
                <li key={f.label} className="inline-flex items-center gap-2">
                  <span className="uppercase tracking-[0.16em] text-zinc-600">{f.label}</span>
                  <span className="text-zinc-300">{f.value}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-6 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
          <TextZoomControl />
          <Link
            to="/accessibility"
            className="text-[11px] tracking-[0.14em] uppercase text-zinc-500 hover:text-white"
            data-testid="footer-accessibility-utility-link"
          >
            Accessibility statement →
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
